import { Dispatch, ReactNode, SetStateAction, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { formatPostCode } from '@/utils/helpers';
import Label from '@/components/fields/Label';
import { useTheme } from '@/contexts/ThemeContext';

const ENDPOINT = process.env.NEXT_PUBLIC_API_ENDPOINT;

export default function PostCodeField({
  id,
  children,
  isRequired,
  className,
  defaultValue = '',
  setShowAddress,
  setManualAddress,
}: {
  id: string;
  children: ReactNode;
  className?: string;
  isRequired: boolean;
  defaultValue?: string;
  setShowAddress: Dispatch<SetStateAction<boolean>>;
  setManualAddress: Dispatch<SetStateAction<boolean>>;
}): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [addressIndex, setAddressIndex] = useState(0);
  const [postcode, setPostcode] = useState(defaultValue);
  const [error, setError] = useState('');
  const [addresses, setAddresses] = useState([]);
  const { setValue, clearErrors } = useFormContext();
  const { landingPage } = useTheme();

  const isDlAssistant = landingPage === 'v12' || landingPage === 'v13';

  const fetchPostCode = () => {
    setShowAddress(false);
    setManualAddress(false);
    setError('');

    if (!postcode) {
      setError('Please specify a postcode to lookup');
      return;
    }

    fetch(`${ENDPOINT}/postcode/${postcode.replace(' ', '')}`)
      .then((response) => response.json())
      .then((data) => {
        const { success, addresses = [] } = data;
        if (success) {
          if (addresses.length) {
            setAddresses(addresses);
            setAddressIndex(0);
            setIsOpen(true);
          } else {
            setError('No address was found for this postcode');
          }
        } else {
          setError(
            "Couldn't lookup your postcode, please try again or enter your address manually"
          );
        }
      })
      .catch(() => {
        setError(
          "Couldn't lookup your postcode, please enter your address manually"
        );
      });
  };

  const handleAddressSelect = async () => {
    const fieldID = id.replace('postcode_lookup_', '');
    const address = addresses[addressIndex];
    const { town_or_city, county, country, formatted_address, line_1, line_2 } =
      address;
    let building_name_number = formatted_address[0];
    const building_number = formatted_address[0].split(' ')[0];

    if (building_number.match(/[0-9]+/)) {
      building_name_number = building_number;
    }

    setValue(`${fieldID}_building_name_number`, `${building_name_number}`);
    setValue(`${fieldID}_street_name`, `${line_1}`);
    setValue(`${fieldID}_address_line_2`, `${line_2}`);
    setValue(`${fieldID}_town`, town_or_city);
    setValue(`${fieldID}_postcode`, postcode);
    setValue(`${fieldID}_county`, county);

    setShowAddress(true);

    switch (country.toLowerCase()) {
      case 'england':
        setValue(`${fieldID}_country`, 'GBR01');
        break;
      case 'scotland':
        setValue(`${fieldID}_country`, 'GBR02');
        break;
      case 'wales':
        setValue(`${fieldID}_country`, 'GBR03');
        break;
      case 'northern ireland':
        setValue(`${fieldID}_country`, 'GBR04');
        break;
      case 'isle of man':
        setValue(`${fieldID}_country`, 'GBR05');
        break;
      case 'guernsey':
        setValue(`${fieldID}_country`, 'GBR06');
        break;
      case 'jersey':
        setValue(`${fieldID}_country`, 'GBR08');
        break;
      case 'default':
        setValue(`${fieldID}_country`, 'GBR');
        break;
    }
    setIsOpen(false);
    clearErrors();
  };

  return (
    <>
      {isOpen ? (
        <div className="fixed bottom-0 inset-x-0 px-4 pb-6 z-10 sm:inset-0 sm:p-0 sm:flex sm:items-center sm:justify-center">
          <div className="fixed inset-0 transition-opacity">
            <div className="absolute inset-0 bg-gray-400 opacity-75"></div>
          </div>

          <div className="bg-white rounded-lg px-4 pt-5 pb-4 overflow-hidden shadow-xl transform transition-all sm:max-w-sm sm:w-full sm:p-6">
            <div>
              <div className="mt-3 text-center sm:mt-5">
                <div className="mt-2">
                  <p className="text-sm leading-5">
                    <select
                      className="w-full px-3 py-2 rounded-md border border-gray"
                      onChange={(e) =>
                        setAddressIndex(parseInt(e.target.value))
                      }
                    >
                      <option value="" disabled>
                        Select...
                      </option>
                      {addresses.map(({ formatted_address }, key) => {
                        formatted_address = formatted_address
                          .filter((val) => val)
                          .join(', ');
                        return (
                          <option key={key} value={key}>
                            {formatted_address}
                          </option>
                        );
                      })}
                    </select>
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-6">
              <span className="flex w-full rounded-md shadow-sm">
                <button
                  type="button"
                  className={`inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2  text-base leading-6 font-medium text-white shadow-sm  focus:outline-none ${
                    isDlAssistant
                      ? 'bg-red-400 focus:bg-red-500 hover:bg-red-500'
                      : 'bg-primary focus:bg-primary-dark  focus:shadow-outline-indigo hover:bg-primary-dark'
                  }  transition ease-in-out duration-150 sm:text-sm sm:leading-5`}
                  onClick={handleAddressSelect}
                >
                  Select Address
                </button>
              </span>
            </div>
          </div>
        </div>
      ) : null}
      <div className={`mb-4 md:flex md:items-center ${className}`}>
        <Label id={id} isRequired={isRequired} className="md:w-2/5 md:pr-4">
          {children}
        </Label>
        <div className="md:w-2/5 md:pl-4">
          <div className="flex justify-between">
            <input
              className="w-5/12 md:w-1/2 mr-2 px-3 py-1 rounded-md border border-gray"
              type="text"
              name={id}
              id={id}
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              onBlur={() => setPostcode(formatPostCode(postcode))}
            />
            <button
              type="button"
              className={`w-7/12 text-sm px-2 md:w-1/2 md:px-4 ${
                isDlAssistant
                  ? 'bg-red-400 border-red-400 hover:text-red-400'
                  : 'bg-primary border-primary  hover:text-primary'
              }   ml-2 text-white rounded-md border-2 transition duration-500 ease-in-out hover:bg-white`}
              onClick={fetchPostCode}
            >
              Lookup PostCode
            </button>
          </div>
          {error ? (
            <p className="mt-2 text-red-600 text-sm font-medium">
              {error}{' '}
              <a
                href="#"
                className="text-cool-gray-500"
                onClick={() => {
                  setManualAddress(true);
                  setError('');
                }}
              >
                Add address manually
              </a>
            </p>
          ) : null}
        </div>
      </div>
    </>
  );
}
